import { useEffect, useRef } from "react";
import { driver, type DriveStep } from "driver.js";

import { useAuth } from "../../hooks/useAuth";

type UseCoachOverlayOptions = {
  overlayId: string;
  steps: DriveStep[];
  enabled?: boolean;
};

function getOverlaySeenKey(overlayId: string, userId: string): string {
  return `onboarding_overlay_${overlayId}_${userId}`;
}

export const useCoachOverlay = ({ overlayId, steps, enabled = true }: UseCoachOverlayOptions) => {
  const { user } = useAuth();
  const userId = user?.id;
  const hasRun = useRef(false);

  useEffect(() => {
    if (!enabled || !userId || hasRun.current || steps.length === 0) return;

    // Check if already seen
    try {
      if (localStorage.getItem(getOverlaySeenKey(overlayId, userId)) === "true") return;
    } catch {
      return;
    }

    hasRun.current = true;

    // Delay so DOM elements have time to render
    const timeout = setTimeout(() => {
      // Filter out steps whose target element doesn't exist in the DOM
      const validSteps = steps.filter((step) => {
        if (!step.element) return true;
        return document.querySelector(step.element as string) !== null;
      });

      if (validSteps.length === 0) {
        hasRun.current = false;
        return;
      }

      const isSingleStep = validSteps.length === 1;

      const driverObj = driver({
        showProgress: !isSingleStep,
        showButtons: isSingleStep ? ["close"] : ["next", "previous", "close"],
        animate: true,
        overlayColor: "rgba(0, 0, 0, 0.5)",
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: "onboarding-coach-popover",
        steps: validSteps,
        onDestroyStarted: () => {
          try {
            localStorage.setItem(getOverlaySeenKey(overlayId, userId), "true");
          } catch {
            // silent
          }
          driverObj.destroy();
        },
      });

      driverObj.drive();
    }, 600);

    return () => clearTimeout(timeout);
  }, [overlayId, steps, enabled, userId]);
};
